<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-[1050]">
      <div class="absolute inset-0 bg-black/40" @click="close"></div>
      <div
        class="absolute bg-white flex flex-col p-5 max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:max-h-[85dvh] max-md:rounded-t-[24px] md:top-0 md:right-0 md:bottom-0 md:w-[340px]"
      >
        <!-- header -->
        <div class="flex items-center justify-between">
          <div class="text-[20px] font-bold text-txprimary">Корзина</div>
          <button class="text-txopacity hover:text-txprimary transition-colors" @click="close">
            <Icon name="lucide:x" class="text-xl" />
          </button>
        </div>
        <div class="text-[14px] text-txopacity mt-1">
          {{ basket.length ? channelsCountLabel : "Пока пусто" }}
        </div>

        <!-- items -->
        <div class="flex-1 min-h-0 overflow-y-auto mt-4 flex flex-col gap-2">
          <template v-if="basket.length">
            <div
              v-for="(item, i) in basket"
              :key="`${item.id}-${item.format?.priceType || i}`"
              class="bg-bgnew rounded-[16px] p-3 flex items-start justify-between gap-3 flex-shrink-0"
            >
              <div class="min-w-0">
                <div class="text-[14px] font-medium text-txprimary line-clamp-1">
                  {{ item.name }}
                </div>
                <div class="text-[12px] text-txopacity mt-0.5">
                  {{ formatLabel(item)
                  }}<span v-if="(item.quantity || 1) > 1"> × {{ item.quantity }}</span>
                </div>
                <div class="text-[16px] font-bold text-txprimary mt-1.5">
                  {{ itemTotal(item).toLocaleString() }} ₽
                </div>
              </div>
              <button
                class="size-9 rounded-[10px] bg-white flex items-center justify-center flex-shrink-0 text-txopacity hover:text-[#f8465b] transition-colors"
                @click="removeItem(item)"
              >
                <Icon name="mage:trash-fill" class="text-lg" />
              </button>
            </div>

            <!-- total -->
            <div
              class="border border-[#d9e5ff] rounded-[16px] px-4 py-3 flex items-center justify-between flex-shrink-0"
            >
              <span class="text-[14px] text-txopacity">Итого</span>
              <span class="text-[18px] font-bold text-txprimary">{{ total.toLocaleString() }} ₽</span>
            </div>
          </template>

          <!-- empty -->
          <div
            v-else
            class="border border-dashed border-[#d9e5ff] rounded-[16px] px-5 py-8 text-center text-[14px] text-txopacity leading-[1.5]"
          >
            Нажмите «В корзину» у формата или добавьте каналы с витрины — затем перейдите
            к созданию заказа.
          </div>
        </div>

        <!-- footer -->
        <div class="mt-4 flex-shrink-0">
          <button class="text-[14px] mb-3 hover:opacity-70 transition-opacity" :style="{ color }" @click="close">
            Добавить канала с витрины
          </button>
          <button
            class="w-full h-[50px] rounded-[12px] text-white text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
            :style="{ backgroundColor: color }"
            :disabled="!basket.length"
            @click="checkout"
          >
            Оформить заказ
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
  import { useBasketStore } from "~/store/BasketModule";

  const props = withDefaults(defineProps<{ open: boolean; color?: string }>(), {
    color: "#3176FF",
  });
  const emit = defineEmits(["update:open"]);

  const basketStore = useBasketStore();
  const { basket } = storeToRefs(basketStore);

  const close = () => emit("update:open", false);

  const channelsCountLabel = computed(() => {
    const n = basket.value.length;
    const mod10 = n % 10;
    const mod100 = n % 100;
    let word = "каналов";
    if (mod10 === 1 && mod100 !== 11) word = "канал";
    else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = "канала";
    return `${n} ${word}`;
  });

  const formatLabel = (item: any): string => {
    const map: Record<string, string> = {
      post24h: "Пост 24ч",
      post48h: "Пост 48ч",
      post72h: "Пост 72ч",
      post30d: "Пост 30 дней",
      fixedInTop: "Пост 30 дней",
      repost: "Репост",
      story: "История/клип",
      native: "Нативный пост",
    };
    return map[item.format?.priceType] ?? item.format?.priceType ?? "Формат не выбран";
  };

  const itemTotal = (item: any): number => (item.format?.price || 0) * (item.quantity || 1);

  const total = computed(() => basket.value.reduce((sum: number, item: any) => sum + itemTotal(item), 0));

  const removeItem = (item: any) => {
    basket.value = basket.value.filter(
      (p: any) => !(p.id === item.id && p.format?.priceType === item.format?.priceType)
    );
  };

  const checkout = () => {
    if (!basket.value.length) return;
    navigateTo("/order");
  };
</script>
